import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../../store/hooks';
import {
  deleteDrillAsync,
  getDrillAsync,
  getDrillDetailsAsync,
} from '../../store/drillSlice';
import FullScreenLoadingSpinner from '../shared/FullScreenLoading';
import {Helmet} from 'react-helmet';
import {useTranslation} from 'react-i18next';
import PageTitle from '../shared/PageTitle';
import {
  Box,
  Button,
  Container,
  Grid,
  Modal,
  Paper,
  Typography,
} from '@mui/material';
import styles from '../../styles';
import BackButton from '../shared/BackButton';
import EditDrillDialog from './EditDrillDialog';
import {DataGrid, GridColDef} from '@mui/x-data-grid';
import {getSpeedMultiplier} from '../../services/measurementService';
import CircleIcon from '@mui/icons-material/Circle';
import {PositionGroup} from '../../enums';
import {formatNum, formatTime} from '../../services/formatService';
import {getTime} from '../../services/dateService';
import {
  DrillDetailRecord,
  DrillMetrics,
  TeamSessionSessionEvent,
} from '../../interfaces';
import MiniTrendLine from '../shared/MiniTrendLine';
import SessionVideoPlayer, {
  SessionVideoPlayerItem,
} from '../shared/SessionVideoPlayer';
import {DateTime} from 'luxon';
import DrillDetailsDataGrid from '../shared/DrillDetailsDataGrid';

interface Props {}

const POSITION_GROUPS = [
  PositionGroup.all,
  PositionGroup.forwards,
  PositionGroup.defense,
  PositionGroup.goalies,
] as const;

type TrendMetricKey =
  | 'strideTime'
  | 'speed'
  | 'speedMax'
  | 'load'
  | 'loadPerMin';

const metricsColumns = (
  t: (key: string) => string,
  preferredUnits?: 'imperial' | 'metric',
): GridColDef[] => [
  {
    field: 'positionGroup',
    headerName: t('library.positionGroup'),
    type: 'string',
    valueFormatter: params => t(`position.${params.value}`),
    width: 100,
  },
  {
    field: 'playerLoadAvg',
    headerName: t('library.avgPlayerLoad'),
    width: 140,
    type: 'number',
    valueFormatter: params =>
      formatNum({
        value: params.value,
        multiplier: 0.01,
        decimals: 2,
      }),
  },
  {
    field: 'playerLoadPerMinAvg',
    headerName: t('library.avgPlayerLoadPerMin'),
    width: 170,
    type: 'number',
    valueFormatter: params =>
      formatNum({
        value: params.value,
        multiplier: 0.01,
        decimals: 2,
      }),
  },
  {
    field: 'drillRunCountAvg',
    headerName: t('library.avgRuns'),
    width: 140,
    type: 'number',
    valueFormatter: params => formatNum({value: params.value}),
  },
  {
    field: 'drillRunDurationAvg',
    headerName: t('library.avgRunDuration'),
    width: 150,
    type: 'number',
    valueFormatter: params => formatTime(params.value),
  },
  {
    field: 'speedAvgAvg',
    headerName: t('library.avgSpeed'),
    width: 110,
    type: 'number',
    valueFormatter: params =>
      formatNum({
        value: params.value,
        multiplier: getSpeedMultiplier(preferredUnits === 'metric'),
        unit: preferredUnits === 'metric' ? 'kph' : 'mph',
      }),
  },
  {
    field: 'speedMaxAvg',
    headerName: t('library.avgMaxSpeed'),
    width: 130,
    type: 'number',
    valueFormatter: params =>
      formatNum({
        value: params.value,
        multiplier: getSpeedMultiplier(preferredUnits === 'metric'),
        unit: preferredUnits === 'metric' ? 'kph' : 'mph',
      }),
  },
  {
    field: 'accelAvgAvg',
    headerName: t('library.avgAccel'),
    width: 110,
    type: 'number',
    valueFormatter: params =>
      formatNum({
        value: params.value,
        multiplier: 1 / 9.8,
        decimals: 2,
        unit: 'g',
      }),
  },
  {
    field: 'accelMaxAvg',
    headerName: t('library.avgMaxAccel'),
    width: 130,
    type: 'number',
    valueFormatter: params =>
      formatNum({
        value: params.value,
        multiplier: 1 / 9.8,
        decimals: 2,
        unit: 'g',
      }),
  },
  {
    field: 'strideRateAvgAvg',
    headerName: t('library.avgStrideRate'),
    width: 140,
    type: 'number',
    valueFormatter: params =>
      formatNum({
        value: params.value,
        decimals: 0,
        unit: 'spm',
      }),
  },
  {
    field: 'balanceNormalizedAvg',
    headerName: t('library.avgBalance'),
    width: 120,
    type: 'number',
    valueFormatter: params =>
      formatNum({
        value: params.value,
        decimals: 0,
        multiplier: 100,
        unit: '%',
      }),
  },
  {
    field: 'hrAvgAvg',
    headerName: t('library.avgHR'),
    width: 100,
    type: 'number',
    valueFormatter: params =>
      formatNum({
        value: params.value,
        decimals: 0,
        unit: 'bpm',
      }),
  },
  {
    field: 'hrMaxAvg',
    headerName: t('library.avgMaxHR'),
    width: 110,
    type: 'number',
    valueFormatter: params =>
      formatNum({
        value: params.value,
        decimals: 0,
        unit: 'bpm',
      }),
  },
  {
    field: 'hrMinAvg',
    headerName: t('library.avgMinHR'),
    width: 110,
    type: 'number',
    valueFormatter: params =>
      formatNum({
        value: params.value,
        decimals: 0,
        unit: 'bpm',
      }),
  },
];

const DrillDetail: React.FC<Props> = () => {
  const {t} = useTranslation();
  const {drillId, teamId} = useParams();
  const navigate = useNavigate();
  const drill = useAppSelector(state => state.drill.drill);
  const drillDetails = useAppSelector(state => state.drill.drillDetails);
  const dispatch = useAppDispatch();
  const [moreInfoOpen, setMoreInfoOpen] = useState(false);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState<string | null>(null);
  const preferredUnits = useAppSelector(
    state => state.profile.profile?.preferredUnits,
  );
  const teamName =
    (useAppSelector(state => state.team.teams) ?? []).find(
      t => t.id === Number(teamId),
    )?.name ?? '';

  useEffect(() => {
    if (drillId && (!drill || drill.id !== Number(drillId))) {
      dispatch(getDrillAsync({id: Number(drillId)}));
    }
  }, [drillId, drill, dispatch]);

  useEffect(() => {
    if (drillId) {
      dispatch(getDrillDetailsAsync({drillId: Number(drillId)}));
    }
  }, [drillId, dispatch]);

  const title = useMemo(
    () =>
      t('navigation.pageTitle', {
        pageName: t('pages.library'),
        drillName: drill?.name,
      }),
    [drill?.name, t],
  );

  const handleDeleteDrill = useCallback(async () => {
    if (!drill) {
      return;
    }
    const res = confirm(t('library.deleteWarning') ?? '');
    if (res) {
      await dispatch(deleteDrillAsync({id: drill.id}));
      navigate(`/teams/${teamId}/library`);
    }
  }, [drill, dispatch, navigate, teamId, t]);

  const metricRows = useMemo(() => {
    const positionGroupOrder: Record<string, number> = {
      [PositionGroup.all]: 0,
      [PositionGroup.forwards]: 1,
      [PositionGroup.defense]: 2,
      [PositionGroup.goalies]: 3,
    };

    return [...(drill?.drillMetrics ?? [])].sort((a, b) => {
      const aOrder =
        positionGroupOrder[a.positionGroup] ?? Number.MAX_SAFE_INTEGER;
      const bOrder =
        positionGroupOrder[b.positionGroup] ?? Number.MAX_SAFE_INTEGER;
      return aOrder - bOrder;
    });
  }, [drill?.drillMetrics]);

  const sortedDetailsByGroup = useMemo(() => {
    const byGroup: Record<string, DrillDetailRecord[]> = {};

    Object.values(PositionGroup).forEach(group => {
      byGroup[group] = [];
    });

    (drillDetails ?? []).forEach(detail => {
      if (!byGroup[detail.positionGroup]) {
        byGroup[detail.positionGroup] = [];
      }
      byGroup[detail.positionGroup].push(detail);
    });

    Object.keys(byGroup).forEach(group => {
      byGroup[group].sort((a, b) => {
        const aStart =
          a.teamSessionSessionEvent?.teamSession?.startTime ??
          a.teamSessionSessionEvent?.startTime ??
          '';
        const bStart =
          b.teamSessionSessionEvent?.teamSession?.startTime ??
          b.teamSessionSessionEvent?.startTime ??
          '';
        return new Date(aStart).getTime() - new Date(bStart).getTime();
      });
    });

    return byGroup;
  }, [drillDetails]);

  const metricsByGroup = useMemo(() => {
    const byGroup: Record<string, DrillMetrics | undefined> = {};

    metricRows.forEach(metric => {
      byGroup[metric.positionGroup] = metric;
    });

    return byGroup;
  }, [metricRows]);

  const drillVideos = useMemo<DrillDetailRecord[]>(() => {
    const allDetails = drillDetails ?? [];
    const seenVideoUrls = new Set<string>();

    return allDetails
      .filter(detail => {
        if (!detail.videoUrl || seenVideoUrls.has(detail.videoUrl)) {
          return false;
        }
        seenVideoUrls.add(detail.videoUrl);
        return true;
      })
      .sort((a, b) => {
        const aTime =
          a.teamSessionSessionEvent?.startTime ??
          a.teamSessionSessionEvent?.teamSession?.startTime ??
          '';
        const bTime =
          b.teamSessionSessionEvent?.startTime ??
          b.teamSessionSessionEvent?.teamSession?.startTime ??
          '';
        return new Date(bTime).getTime() - new Date(aTime).getTime();
      });
  }, [drillDetails]);

  const drillVideoItems = useMemo<
    (SessionVideoPlayerItem & Partial<DrillDetailRecord>)[]
  >(
    () =>
      drillVideos.map(video => ({
        id: video.id,
        videoUrl: video.videoUrl,
        thumbnailUrl: video.thumbnailUrl,
        playerLoadAvg: video.playerLoadAvg,
        teamSessionSessionEvent: {
          startTime: video.teamSessionSessionEvent?.startTime,
          endTime: video.teamSessionSessionEvent?.endTime,
        },
      })),
    [drillVideos],
  );

  const getTrendValues = useCallback(
    (group: PositionGroup, metricKey: TrendMetricKey): number[] => {
      const groupDetails = sortedDetailsByGroup[group] ?? [];

      return groupDetails
        .map(detail => {
          if (metricKey === 'strideTime') {
            return detail.strintTotalTimeAvg;
          }
          if (metricKey === 'speed') {
            return detail.speedAvgAvg;
          }
          if (metricKey === 'speedMax') {
            return detail.speedMaxAvg;
          }
          if (metricKey === 'load') {
            return detail.playerLoadAvg;
          }
          if (metricKey === 'loadPerMin') {
            return detail.playerLoadPerMinAvg;
          }
          return detail.playerLoadAvg;
        })
        .filter(
          (value): value is number => value !== undefined && value !== null,
        );
    },
    [sortedDetailsByGroup],
  );

  if (!drill) {
    return (
      <>
        <Helmet>
          <title>{title}</title>
        </Helmet>
        <FullScreenLoadingSpinner />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Container>
        <PageTitle>{drill.name}</PageTitle>
        <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
          <BackButton navigationUrl={`/teams/${teamId}/library`}>
            {t('pages.library')}
          </BackButton>
          <Box>
            <Button
              variant="outlined"
              // size="small"
              onClick={() => setMoreInfoOpen(true)}
              sx={{mr: 2}}>
              {t('library.moreInfo')}
            </Button>
            <Button variant="outlined" onClick={handleDeleteDrill} sx={{mr: 2}}>
              {t('library.deleteDrill')}
            </Button>
            <EditDrillDialog drill={drill} />
          </Box>
        </Box>
        <Modal
          open={moreInfoOpen}
          onClose={() => setMoreInfoOpen(false)}
          aria-labelledby="drill-detail-info-title">
          <Box sx={styles.modal}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
              }}>
              <Typography variant="h5" id="drill-detail-info-title">
                {t('library.columnDescriptions')}
              </Typography>
              <Button size="small" onClick={() => setMoreInfoOpen(false)}>
                {t('general.close')}
              </Button>
            </Box>
            {[
              {
                label: t('library.sessionCount'),
                desc: t('library.sessionCountDesc'),
              },
              {
                label: t('library.practiceCount'),
                desc: t('library.practiceCountDesc'),
              },
              {
                label: t('library.skillsCount'),
                desc: t('library.skillsCountDesc'),
              },
              {
                label: t('library.sessionDate'),
                desc: t('library.sessionDateDesc'),
              },
              {
                label: t('general.type'),
                desc: t('library.sessionTypeDesc'),
              },
              {
                label: t('general.duration'),
                desc: t('library.durationDesc'),
              },
              {
                label: t('library.avgDuration'),
                desc: t('library.avgDurationDesc'),
              },
              {
                label: t('library.avgPlayerLoad'),
                desc: t('library.avgPlayerLoadDesc'),
              },
              {label: t('library.avgRuns'), desc: t('library.avgRunsDesc')},
              {
                label: t('library.avgRunDuration'),
                desc: t('library.avgRunDurationDesc'),
              },
              {label: t('library.avgSpeed'), desc: t('library.avgSpeedDesc')},
              {
                label: t('library.avgMaxSpeed'),
                desc: t('library.avgMaxSpeedDesc'),
              },
              {label: t('library.avgAccel'), desc: t('library.avgAccelDesc')},
              {
                label: t('library.avgMaxAccel'),
                desc: t('library.avgMaxAccelDesc'),
              },
              {
                label: t('library.avgStrideRate'),
                desc: t('library.avgStrideRateDesc'),
              },
              {
                label: t('library.avgBalance'),
                desc: t('library.avgBalanceDesc'),
              },
              {label: t('library.avgHR'), desc: t('library.avgHRDesc')},
              {label: t('library.avgMaxHR'), desc: t('library.avgMaxHRDesc')},
              {label: t('library.avgMinHR'), desc: t('library.avgMinHRDesc')},
            ].map(({label, desc}) => (
              <Box key={label} sx={{mb: 1.5}}>
                <Typography variant="subtitle2">{label}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {desc}
                </Typography>
              </Box>
            ))}
          </Box>
        </Modal>

        <Box>
          <Paper sx={{p: 2}}>
            {drill.description && (
              <Box sx={{mb: 2}}>
                <Typography variant="h4" sx={{mb: 1}}>
                  {t('general.description')}
                </Typography>
                <Typography variant="body1">{drill.description}</Typography>
              </Box>
            )}
            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 2}}>
              <Box sx={{display: 'flex', flexDirection: 'column'}}>
                <Typography variant="caption">
                  {t('library.sessionCount')}
                </Typography>
                <Typography variant="h4">
                  {drill.drillMetrics?.find(
                    metric => metric.positionGroup === PositionGroup.all,
                  )?.count ?? '-'}
                </Typography>
              </Box>
              <Box sx={{display: 'flex', flexDirection: 'column'}}>
                <Typography variant="caption">
                  {t('library.practiceCount')}
                </Typography>
                <Typography variant="h4">
                  {drill.drillMetrics?.find(
                    metric => metric.positionGroup === PositionGroup.all,
                  )?.practiceCount ?? '-'}
                </Typography>
              </Box>
              <Box sx={{display: 'flex', flexDirection: 'column'}}>
                <Typography variant="caption">
                  {t('library.skillsCount')}
                </Typography>
                <Typography variant="h4">
                  {drill.drillMetrics?.find(
                    metric => metric.positionGroup === PositionGroup.all,
                  )?.skillsCount ?? '-'}
                </Typography>
              </Box>
              <Box sx={{display: 'flex', flexDirection: 'column'}}>
                <Typography variant="caption">
                  {t('library.avgDuration')}
                </Typography>
                <Typography variant="h4">
                  {getTime(
                    drill.drillMetrics?.find(
                      metric => metric.positionGroup === PositionGroup.all,
                    )?.durationAvg,
                  ) ?? '-'}
                </Typography>
              </Box>
              {(() => {
                const allMetric = drill.drillMetrics?.find(
                  metric => metric.positionGroup === PositionGroup.all,
                );
                const score = allMetric?.drillLoadScore;
                const color =
                  score === 1
                    ? 'success.main'
                    : score === 2
                      ? 'warning.main'
                      : score === 3
                        ? 'error.main'
                        : undefined;
                const label =
                  score === 1
                    ? t('library.lowLoad')
                    : score === 2
                      ? t('library.mediumLoad')
                      : score === 3
                        ? t('library.highLoad')
                        : '-';
                return (
                  <Box sx={{display: 'flex', flexDirection: 'column'}}>
                    <Typography variant="caption">
                      {t('library.drillLoadScore')}
                    </Typography>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                      {color && <CircleIcon sx={{fontSize: 16, color}} />}
                      <Typography variant="h4">{label}</Typography>
                    </Box>
                  </Box>
                );
              })()}
            </Box>
          </Paper>
        </Box>
        <Box sx={{mt: 3}}>
          <Grid container spacing={3}>
            {POSITION_GROUPS.map(group => {
              const groupMetric = metricsByGroup[group];
              const playerLoadTrends = getTrendValues(group, 'load').map(
                value => value * 0.01,
              );
              const loadPerMinTrends = getTrendValues(group, 'loadPerMin').map(
                value => value * 0.01,
              );
              const speedTrends = getTrendValues(group, 'speed').map(
                value =>
                  value * getSpeedMultiplier(preferredUnits === 'metric'),
              );
              const strideTimeTrends = getTrendValues(group, 'strideTime');

              return (
                <Grid item xs={12} sm={6} lg={3} key={group}>
                  <Paper sx={{p: 2}}>
                    <Typography variant="h4" sx={{mb: 2}}>
                      {t(`position.${group}`)}
                    </Typography>

                    <Box
                      sx={{
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}>
                      <Box sx={{flexShrink: 0}}>
                        <Typography variant="caption" color="text.secondary">
                          {t('library.avgPlayerLoad')}
                        </Typography>
                        <Typography variant="body1" sx={{fontWeight: 600}}>
                          {formatNum({
                            value: groupMetric?.playerLoadAvg,
                            multiplier: 0.01,
                            decimals: 2,
                          }) ?? '-'}
                        </Typography>
                      </Box>
                      <Box sx={{flex: 1, minWidth: 0}}>
                        <MiniTrendLine values={playerLoadTrends} />
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}>
                      <Box sx={{flexShrink: 0}}>
                        <Typography variant="caption" color="text.secondary">
                          {t('library.avgPlayerLoadPerMin')}
                        </Typography>
                        <Typography variant="body1" sx={{fontWeight: 600}}>
                          {formatNum({
                            value: groupMetric?.playerLoadPerMinAvg,
                            multiplier: 0.01,
                            decimals: 2,
                          }) ?? '-'}
                        </Typography>
                      </Box>
                      <Box sx={{flex: 1, minWidth: 0}}>
                        <MiniTrendLine values={loadPerMinTrends} />
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}>
                      <Box sx={{flexShrink: 0}}>
                        <Typography variant="caption" color="text.secondary">
                          {t('library.avgSpeed')}
                        </Typography>
                        <Typography variant="body1" sx={{fontWeight: 600}}>
                          {formatNum({
                            value: groupMetric?.speedAvgAvg,
                            multiplier: getSpeedMultiplier(
                              preferredUnits === 'metric',
                            ),
                            unit: preferredUnits === 'metric' ? 'kph' : 'mph',
                          }) ?? '-'}
                        </Typography>
                      </Box>
                      <Box sx={{flex: 1, minWidth: 0}}>
                        <MiniTrendLine values={speedTrends} />
                      </Box>
                    </Box>

                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                      <Box sx={{flexShrink: 0}}>
                        <Typography variant="caption" color="text.secondary">
                          {t('library.avgStrintTotalTime')}
                        </Typography>
                        <Typography variant="body1" sx={{fontWeight: 600}}>
                          {formatTime(groupMetric?.strintTotalTimeAvg) ?? '-'}
                        </Typography>
                      </Box>
                      <Box sx={{flex: 1, minWidth: 0}}>
                        <MiniTrendLine values={strideTimeTrends} />
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>
        <Box sx={{mt: 3}}>
          <Paper sx={{p: 2}}>
            <Typography variant="h3" sx={{mb: 2}}>
              {t('library.drillMetrics')}
            </Typography>
            <Box>
              <DataGrid
                autoHeight
                rows={metricRows}
                columns={metricsColumns(t, preferredUnits)}
                density="compact"
                disableSelectionOnClick
                hideFooter
              />
            </Box>
          </Paper>
        </Box>

        <Box sx={{mt: 3}}>
          {drillVideos.length > 0 && (
            <Paper sx={{p: 2, mb: 3}}>
              <Typography variant="h3" sx={{mb: 2}}>
                {t('library.drillVideos')}
              </Typography>
              <SessionVideoPlayer
                videoItems={drillVideoItems}
                selectedVideoUrl={selectedVideoUrl}
                renderCard={({item, active, onClick}) => {
                  const video = item as DrillDetailRecord;
                  return (
                    <Box
                      onClick={onClick}
                      sx={{
                        width: 210,
                        minWidth: 210,
                        mx: 1,
                        borderRadius: 1,
                        border: theme =>
                          `2px solid ${
                            active
                              ? theme.palette.primary.main
                              : theme.palette.divider
                          }`,
                        cursor: 'pointer',
                        overflow: 'hidden',
                      }}>
                      {video.thumbnailUrl ? (
                        <img
                          src={video.thumbnailUrl}
                          style={{
                            width: '100%',
                            height: 100,
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: '100%',
                            height: 100,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'grey.100',
                          }}>
                          <Typography variant="caption">
                            {t('general.video')}
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{p: 1}}>
                        {video.teamSessionSessionEvent?.startTime && (
                          <Typography
                            variant="body1"
                            sx={{
                              display: 'block',
                              fontWeight: 700,
                              textTransform: 'capitalize',
                            }}>
                            {DateTime.fromISO(
                              video.teamSessionSessionEvent?.startTime,
                            ).toFormat('M/d/yyyy')}
                          </Typography>
                        )}
                        {video.teamSessionSessionEvent?.startTime &&
                          video.teamSessionSessionEvent?.endTime && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{display: 'block'}}>
                              {t('general.duration')}:{' '}
                              {formatTime(
                                DateTime.fromISO(
                                  video.teamSessionSessionEvent.endTime,
                                ).diff(
                                  DateTime.fromISO(
                                    video.teamSessionSessionEvent.startTime,
                                  ),
                                  'seconds',
                                ).seconds,
                              )}
                            </Typography>
                          )}
                        {video.playerLoadAvg != null && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{display: 'block'}}>
                            {t('library.avgPlayerLoad')}:{' '}
                            {formatNum({
                              value: video.playerLoadAvg,
                              multiplier: 0.01,
                            })}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                }}
              />
            </Paper>
          )}
        </Box>

        <DrillDetailsDataGrid
          data={(drillDetails ?? []).map(drillDetail => ({
            drillDetail,
            teamSession: drillDetail.teamSessionSessionEvent?.teamSession,
            teamSessionSessionEvent:
              drillDetail.teamSessionSessionEvent as TeamSessionSessionEvent,
          }))}
          page="drillDetail"
          preferredUnits={preferredUnits}
          title={t('library.drillDetails')}
          onVideoSelect={setSelectedVideoUrl}
          drillName={drill?.name}
          teamName={teamName}
        />
      </Container>
    </>
  );
};

export default DrillDetail;
